import { addressRegionOriginSchema } from './address-resolution.js';
import { z } from 'zod';
import { sirenInputSchema, siretInputSchema, refineFrenchCompanyIdentifiers, } from './french-company-identifiers.js';
import { geographicSubdivisionFields, validateSubdivision } from './geographic-subdivision.js';
import { deliveryPlanningSchema } from './delivery-planning.js';
import { CUSTOMER_INDUSTRY_CODE_MAX_LENGTH, CUSTOMER_SORT_FIELDS, CUSTOMER_TAGS_MAX_COUNT, CUSTOMER_TAG_MAX_LENGTH, CUSTOMER_EXPORT_FORMATS, } from '../constants/customers.js';
import { DOCUMENT_LANGUAGES } from '../constants/documents.js';
import { validateVatId } from '../utils/vat-id.js';
import { commaSeparated, countryCodeSchema, currencyCodeSchema, idSchema, isoDateTimeSchema, moneySchema, percentSchema, } from './common.js';
import { customFieldValuesSchema } from './custom-field.js';
import { addressTypeSchema, customerStatusSchema, customerTypeSchema, partnerRiskStatusSchema, taxCategorySchema, vatIdStatusSchema, } from './enums.js';
import { paginationMetaSchema, paginationQuerySchema, sortOrderSchema } from './pagination.js';
export const customerIndustrySchema = z
    .string()
    .trim()
    .min(1)
    .max(50)
    .regex(/^[A-Z][A-Z0-9_]*$/u);
/** Classification codes are opaque text; only surrounding whitespace is normalized. */
export const customerIndustryCodeSchema = z.string().trim().max(CUSTOMER_INDUSTRY_CODE_MAX_LENGTH);
export const customerIndustryCodeInputSchema = customerIndustryCodeSchema
    .transform((value) => (value === '' ? null : value))
    .nullish();
export const customerTagSchema = z.string().trim().min(1).max(CUSTOMER_TAG_MAX_LENGTH);
export const customerTagsSchema = z.array(customerTagSchema).max(CUSTOMER_TAGS_MAX_COUNT);
const optionalText = (max) => z.string().trim().max(max).nullish();
const optionalAddressContactText = (max) => optionalText(max).transform((value) => (value === '' ? null : value));
export const vatIdInputSchema = z
    .string()
    .trim()
    .min(4)
    .max(20)
    .superRefine((value, ctx) => {
    const result = validateVatId(value);
    if (!result.valid) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: result.reason === 'CHECKSUM_INVALID'
                ? 'Die Prüfziffer der USt-IdNr. ist falsch.'
                : 'Die USt-IdNr. entspricht keinem gültigen EU-Format.',
        });
    }
})
    .nullish();
// ---------------------------------------------------------------------------
// Kunde
// ---------------------------------------------------------------------------
export const customerSchema = z.object({
    hasMigrationSource: z.boolean().optional(),
    migrationReviewRequired: z.boolean().optional(),
    id: idSchema,
    customerNumber: z.string(),
    type: customerTypeSchema,
    status: customerStatusSchema,
    companyName: z.string().nullable(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    displayName: z.string(),
    searchTerm: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    website: z.string().nullable(),
    street: z.string().nullable(),
    geocodingPublic: z.boolean().default(false),
    houseNumber: z.string().nullable(),
    addressLine2: z.string().nullable(),
    postalCode: z.string().nullable(),
    city: z.string().nullable(),
    region: z.string().nullable(),
    regionOrigin: addressRegionOriginSchema,
    ...geographicSubdivisionFields,
    countryCode: z.string().nullable(),
    vatId: z.string().nullable(),
    vatIdStatus: vatIdStatusSchema,
    vatIdCheckedAt: isoDateTimeSchema.nullable(),
    taxNumber: z.string().nullable(),
    siren: z.string().nullish(),
    siret: z.string().nullish(),
    gln: z.string().nullable(),
    eoriNumber: z.string().nullable(),
    taxCategory: taxCategorySchema.nullable(),
    currency: z.string(),
    documentLanguage: z.string().nullable(),
    priceGroupId: idSchema.nullish(),
    priceGroupName: z.string().nullish(),
    priceGroup: z.string().nullable(),
    defaultDiscountPct: z.string().nullable(),
    paymentTermId: idSchema.nullable(),
    paymentTermCode: z.string().nullable(),
    paymentTermLabel: z.string().nullable(),
    paymentTermsDays: z.number().int().nullable(),
    cashDiscountPct: z.string().nullable(),
    cashDiscountDays: z.number().int().nullable(),
    deliveryTerms: z.string().nullable(),
    creditLimit: z.string().nullable(),
    deliveryBlock: z.boolean(),
    dunningBlock: z.boolean(),
    minRemainingShelfLifeDays: z.number().int().nullable(),
    datevDebtorAccount: z.string().nullable(),
    /** Risikoeinstufung des Debitors; sperrt Belegerstellung und Lieferung. */
    riskStatus: partnerRiskStatusSchema,
    isBlocked: z.boolean(),
    blockReason: z.string().nullable(),
    industry: customerIndustrySchema.nullable(),
    industryCode: customerIndustryCodeSchema.nullable(),
    salesTerritory: z.string().nullable(),
    revenueClass: z.string().nullable(),
    ownerUserId: idSchema.nullable(),
    ownerName: z.string().nullable(),
    tags: z.array(z.string()),
    customFields: customFieldValuesSchema,
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const customerListItemSchema = customerSchema.pick({
    id: true,
    migrationReviewRequired: true,
    hasMigrationSource: true,
    customerNumber: true,
    type: true,
    status: true,
    displayName: true,
    email: true,
    phone: true,
    postalCode: true,
    city: true,
    countryCode: true,
    priceGroupId: true,
    priceGroupName: true,
    priceGroup: true,
    subdivisionCode: true,
    subdivisionName: true,
    industry: true,
    industryCode: true,
    salesTerritory: true,
    revenueClass: true,
    vatId: true,
    siren: true,
    siret: true,
    vatIdStatus: true,
    ownerName: true,
    tags: true,
    customFields: true,
    riskStatus: true,
    isBlocked: true,
    blockReason: true,
    createdAt: true,
    updatedAt: true,
});
export const customerWriteSchema = z.object({
    /** Leer lassen: Die Nummer wird aus dem Nummernkreis CUSTOMER vergeben. */
    customerNumber: z.string().trim().min(1).max(30).nullish(),
    type: customerTypeSchema,
    status: customerStatusSchema.default('ACTIVE'),
    companyName: optionalText(200),
    firstName: optionalText(100),
    lastName: optionalText(100),
    email: z.string().trim().email().max(200).nullish(),
    phone: optionalText(50),
    website: z.string().trim().url().max(200).nullish(),
    street: optionalText(200),
    geocodingPublic: z.boolean().optional(),
    houseNumber: optionalText(20),
    addressLine2: optionalText(200),
    postalCode: optionalText(20),
    city: optionalText(100),
    region: optionalText(100),
    regionOrigin: addressRegionOriginSchema,
    ...geographicSubdivisionFields,
    countryCode: countryCodeSchema.default('DE'),
    vatId: vatIdInputSchema,
    taxNumber: optionalText(30),
    siren: sirenInputSchema,
    siret: siretInputSchema,
    gln: z
        .string()
        .trim()
        .regex(/^\d{13}$/, 'Die GLN besteht aus 13 Ziffern.')
        .nullish(),
    eoriNumber: optionalText(20),
    taxCategory: taxCategorySchema.default('DOMESTIC'),
    currency: currencyCodeSchema.default('EUR'),
    documentLanguage: z.enum(DOCUMENT_LANGUAGES).default('tr'),
    priceGroupId: idSchema.nullish(),
    priceGroup: optionalText(50),
    defaultDiscountPct: percentSchema.default('0'),
    paymentTermsDays: z.number().int().min(0).max(365).default(14),
    cashDiscountPct: percentSchema.default('0'),
    cashDiscountDays: z.number().int().min(0).max(365).default(0),
    deliveryTerms: optionalText(160),
    creditLimit: moneySchema.nullish(),
    deliveryBlock: z.boolean().default(false),
    dunningBlock: z.boolean().default(false),
    minRemainingShelfLifeDays: z.number().int().min(0).max(3650).nullish(),
    datevDebtorAccount: z
        .string()
        .trim()
        .regex(/^\d{4,9}$/, 'Das DATEV-Debitorenkonto besteht aus 4 bis 9 Ziffern.')
        .nullish(),
    industry: customerIndustrySchema.nullish(),
    industryCode: customerIndustryCodeInputSchema,
    salesTerritory: optionalText(120),
    revenueClass: optionalText(120),
    ownerUserId: idSchema.nullish(),
    tags: customerTagsSchema.default([]),
    customFields: customFieldValuesSchema.default({}),
});
/**
 * Firmen brauchen einen Firmennamen, Privatkunden Vor- und Nachnamen. Die Prüfung
 * läuft zusätzlich serverseitig gegen den zusammengeführten Datensatz, weil ein
 * PATCH nur Teilfelder liefert.
 */
export function hasRequiredNameFields(input) {
    if (input.type === 'COMPANY') {
        return typeof input.companyName === 'string' && input.companyName.trim().length > 0;
    }
    return (typeof input.firstName === 'string' &&
        input.firstName.trim().length > 0 &&
        typeof input.lastName === 'string' &&
        input.lastName.trim().length > 0);
}
export const customerCreateSchema = customerWriteSchema.superRefine((value, ctx) => {
    validateSubdivision(value, ctx);
    refineFrenchCompanyIdentifiers(value, ctx);
    if (!hasRequiredNameFields(value)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: value.type === 'COMPANY' ? ['companyName'] : ['lastName'],
            message: value.type === 'COMPANY'
                ? 'Für Firmenkunden ist der Firmenname erforderlich.'
                : 'Für Privatkunden sind Vor- und Nachname erforderlich.',
        });
    }
});
export const customerUpdateSchema = customerWriteSchema
    .extend({
    countryCode: countryCodeSchema.nullable(),
    taxCategory: taxCategorySchema.nullable(),
    documentLanguage: z.enum(DOCUMENT_LANGUAGES).nullable(),
    defaultDiscountPct: percentSchema.nullable(),
    paymentTermsDays: z.number().int().min(0).max(365).nullable(),
    cashDiscountDays: z.number().int().min(0).max(365).nullable(),
    cashDiscountPct: percentSchema.nullable(),
})
    .partial()
    .superRefine(refineFrenchCompanyIdentifiers);
// ---------------------------------------------------------------------------
// Listenabfrage und Export
// ---------------------------------------------------------------------------
export const customerSortFieldSchema = z.enum(CUSTOMER_SORT_FIELDS);
export const customerListQuerySchema = paginationQuerySchema
    .omit({ sort: true })
    .extend({
    sort: z
        .string()
        .regex(/^[a-zA-Z]+:(asc|desc)$/)
        .optional(),
    status: commaSeparated(customerStatusSchema),
    industry: commaSeparated(customerIndustrySchema),
    industryCode: customerIndustryCodeSchema.optional(),
    tags: commaSeparated(customerTagSchema),
    countryCode: countryCodeSchema.optional(),
    priceGroupIds: commaSeparated(z.union([idSchema, z.literal('NONE')])),
    subdivisionCode: commaSeparated(z.string().trim().min(1).max(20)),
    ownerUserId: idSchema.optional(),
    vatIdStatus: commaSeparated(vatIdStatusSchema),
})
    .transform((value) => {
    const [field, order] = (value.sort ?? 'searchTerm:asc').split(':');
    const parsedField = customerSortFieldSchema.safeParse(field);
    const parsedOrder = sortOrderSchema.safeParse(order);
    return {
        ...value,
        sortField: parsedField.success ? parsedField.data : 'searchTerm',
        sortOrder: parsedOrder.success ? parsedOrder.data : 'asc',
    };
});
export const customerExportQuerySchema = customerListQuerySchema;
export const customerExportFormatSchema = z.enum(CUSTOMER_EXPORT_FORMATS);
export const customerListResponseSchema = z.object({
    data: z.array(customerListItemSchema),
    meta: paginationMetaSchema,
    facets: z.object({
        tags: z.array(z.object({ value: z.string(), count: z.number().int() })),
    }),
});
// ---------------------------------------------------------------------------
// Adressen
// ---------------------------------------------------------------------------
export const customerAddressSchema = z.object({
    hasMigrationSource: z.boolean().optional(),
    purposes: z.array(addressTypeSchema).optional(),
    defaultFor: z.array(addressTypeSchema).optional(),
    id: idSchema,
    customerId: idSchema,
    type: addressTypeSchema,
    addressTypeId: idSchema.nullish(),
    label: z.string().nullable(),
    recipient: z.string().nullable(),
    // Address-local contact details are internal and never general customer contacts.
    contactName: z.string().nullish(),
    contactPosition: z.string().nullish(),
    contactPhone: z.string().nullish(),
    contactMobile: z.string().nullish(),
    contactEmail: z.string().nullish(),
    street: z.string().nullable(),
    geocodingPublic: z.boolean().default(false),
    houseNumber: z.string().nullable(),
    addressLine2: z.string().nullable(),
    postalCode: z.string().nullable(),
    city: z.string().nullable(),
    region: z.string().nullable(),
    regionOrigin: addressRegionOriginSchema,
    ...geographicSubdivisionFields,
    countryCode: z.string().nullable(),
    gln: z.string().nullable(),
    deliveryTermsOverride: z.string().nullable(),
    deliveryPlanning: deliveryPlanningSchema.nullish(),
    isDefault: z.boolean(),
    customFields: customFieldValuesSchema,
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export function addressPurposes(address) {
    return address.purposes?.length ? address.purposes : [address.type];
}
export function addressDefaultPurposes(address) {
    return address.purposes?.length
        ? (address.defaultFor ?? [])
        : address.isDefault
            ? [address.type]
            : [];
}
const customerAddressWriteObjectSchema = z.object({
    type: addressTypeSchema.default('SHIPPING'),
    addressTypeId: idSchema.nullish(),
    label: optionalText(100),
    recipient: optionalText(200),
    contactName: optionalAddressContactText(200),
    contactPosition: optionalAddressContactText(100),
    contactPhone: optionalAddressContactText(50),
    contactMobile: optionalAddressContactText(50),
    contactEmail: z
        .union([z.string().trim().max(254).email(), z.string().trim().length(0)])
        .transform((value) => (value === '' ? null : value))
        .nullish(),
    street: z.string().trim().min(1).max(200),
    geocodingPublic: z.boolean().optional(),
    houseNumber: optionalText(20),
    addressLine2: optionalText(200),
    postalCode: z.string().trim().min(1).max(20),
    city: z.string().trim().min(1).max(100),
    region: optionalText(100),
    regionOrigin: addressRegionOriginSchema,
    ...geographicSubdivisionFields,
    countryCode: countryCodeSchema.default('DE'),
    gln: z
        .string()
        .trim()
        .regex(/^\d{13}$/, 'Die GLN besteht aus 13 Ziffern.')
        .nullish(),
    deliveryTermsOverride: optionalText(160),
    deliveryPlanning: deliveryPlanningSchema.nullish(),
    isDefault: z.boolean().default(false),
    customFields: customFieldValuesSchema.default({}),
});
function validateAddressDeliveryTerms(value, ctx) {
    if (value.type !== undefined && value.type !== 'SHIPPING' && value.deliveryPlanning?.enabled) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['deliveryPlanning'],
            message: 'Delivery planning requires a shipping address.',
        });
    }
    if (value.type !== undefined &&
        value.type !== 'SHIPPING' &&
        value.deliveryTermsOverride !== null &&
        value.deliveryTermsOverride !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['deliveryTermsOverride'],
            message: 'Eine abweichende Lieferkondition ist nur für Lieferadressen zulässig.',
        });
    }
}
export const customerAddressWriteSchema = customerAddressWriteObjectSchema.superRefine((value, ctx) => {
    validateAddressDeliveryTerms(value, ctx);
    validateSubdivision(value, ctx);
});
export const customerAddressUpdateSchema = customerAddressWriteObjectSchema
    .extend({
    street: optionalText(200),
    postalCode: optionalText(20),
    city: optionalText(100),
    countryCode: countryCodeSchema.nullable(),
})
    .partial()
    .superRefine(validateAddressDeliveryTerms);
// ---------------------------------------------------------------------------
// Ansprechpartner
// ---------------------------------------------------------------------------
export const customerContactSchema = z.object({
    hasMigrationSource: z.boolean().optional(),
    id: idSchema,
    customerId: idSchema,
    salutation: z.string().nullable(),
    title: z.string().nullable(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    position: z.string().nullable(),
    department: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    mobile: z.string().nullable(),
    language: z.string().nullable(),
    isPrimary: z.boolean(),
    optOutMarketing: z.boolean(),
    responsibilities: z.array(z.string()),
    responsibilityIds: z.array(idSchema).optional(),
    customFields: customFieldValuesSchema,
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const customerContactWriteSchema = z.object({
    salutation: optionalText(20),
    title: optionalText(30),
    firstName: z.string().trim().min(1).max(100),
    lastName: z.string().trim().min(1).max(100),
    position: optionalText(100),
    department: optionalText(100),
    email: z.string().trim().email().max(200).nullish(),
    phone: optionalText(50),
    mobile: optionalText(50),
    language: z.enum(DOCUMENT_LANGUAGES).default('tr'),
    isPrimary: z.boolean().default(false),
    optOutMarketing: z.boolean().default(false),
    responsibilities: z.array(z.string().trim().min(1).max(120)).max(20).default([]),
    responsibilityIds: z.array(idSchema).max(20).optional(),
    customFields: customFieldValuesSchema.default({}),
});
export const customerContactUpdateSchema = customerContactWriteSchema
    .extend({
    firstName: optionalText(100),
    lastName: optionalText(100),
    language: z.enum(DOCUMENT_LANGUAGES).nullable(),
})
    .partial();
// ---------------------------------------------------------------------------
// Kontaktpräferenzen und Kontaktsperren
// ---------------------------------------------------------------------------
export const customerContactChannels = ['PHONE', 'EMAIL', 'VISIT', 'POSTAL', 'WHATSAPP'];
export const customerContactChannelSchema = z.enum(customerContactChannels);
export const customerContactPreferenceSourceSchema = z.enum([
    'MANUAL',
    'CUSTOMER_REQUEST',
    'IMPORT',
    'LEGAL',
    'LEGACY',
]);
const localContactTimeSchema = z.string().regex(/^(?:[01][0-9]|2[0-3]):[0-5][0-9]$/u);
export const customerContactPreferenceWriteSchema = z
    .object({
    preferredChannel: customerContactChannelSchema.nullable().default(null),
    preferredTimeStart: localContactTimeSchema.nullable().default(null),
    preferredTimeEnd: localContactTimeSchema.nullable().default(null),
    preferredWeekdays: z.array(z.number().int().min(1).max(7)).max(7).default([]),
    allowedChannels: z
        .array(customerContactChannelSchema)
        .max(customerContactChannels.length)
        .default([...customerContactChannels]),
    blockedChannels: z
        .array(customerContactChannelSchema)
        .max(customerContactChannels.length)
        .default([]),
    generalContactBlock: z.boolean().default(false),
    marketingContactBlock: z.boolean().default(false),
    blockReason: z.string().trim().min(1).max(2_000).nullable().default(null),
    validFrom: isoDateTimeSchema.nullable().default(null),
    validUntil: isoDateTimeSchema.nullable().default(null),
    internalNote: z.string().trim().max(5_000).nullable().default(null),
    source: customerContactPreferenceSourceSchema.default('MANUAL'),
    capturedAt: isoDateTimeSchema,
})
    .superRefine((value, context) => {
    const allowed = new Set(value.allowedChannels);
    const blocked = new Set(value.blockedChannels);
    if (allowed.size !== value.allowedChannels.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['allowedChannels'],
            message: 'CONTACT_PREFERENCE_CHANNEL_DUPLICATE',
        });
    }
    if (blocked.size !== value.blockedChannels.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['blockedChannels'],
            message: 'CONTACT_PREFERENCE_CHANNEL_DUPLICATE',
        });
    }
    if (value.blockedChannels.some((channel) => allowed.has(channel))) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['blockedChannels'],
            message: 'CONTACT_PREFERENCE_CHANNEL_CONFLICT',
        });
    }
    if (value.preferredChannel !== null &&
        (!allowed.has(value.preferredChannel) || blocked.has(value.preferredChannel))) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['preferredChannel'],
            message: 'CONTACT_PREFERENCE_PREFERRED_CHANNEL_BLOCKED',
        });
    }
    if ((value.preferredTimeStart === null) !== (value.preferredTimeEnd === null)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['preferredTimeEnd'],
            message: 'CONTACT_PREFERENCE_TIME_PAIR_REQUIRED',
        });
    }
    if (new Set(value.preferredWeekdays).size !== value.preferredWeekdays.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['preferredWeekdays'],
            message: 'CONTACT_PREFERENCE_WEEKDAY_DUPLICATE',
        });
    }
    if (value.validFrom !== null &&
        value.validUntil !== null &&
        new Date(value.validUntil).getTime() <= new Date(value.validFrom).getTime()) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['validUntil'],
            message: 'CONTACT_PREFERENCE_VALIDITY_ORDER',
        });
    }
    if ((value.generalContactBlock ||
        value.marketingContactBlock ||
        value.blockedChannels.length > 0) &&
        value.blockReason === null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['blockReason'],
            message: 'CONTACT_PREFERENCE_BLOCK_REASON_REQUIRED',
        });
    }
});
export const customerContactPreferenceSchema = z.object({
    id: idSchema,
    customerId: idSchema,
    contactId: idSchema.nullable(),
    preferredChannel: customerContactChannelSchema.nullable(),
    preferredTimeStart: localContactTimeSchema.nullable(),
    preferredTimeEnd: localContactTimeSchema.nullable(),
    preferredWeekdays: z.array(z.number().int().min(1).max(7)),
    allowedChannels: z.array(customerContactChannelSchema),
    blockedChannels: z.array(customerContactChannelSchema),
    generalContactBlock: z.boolean(),
    marketingContactBlock: z.boolean(),
    blockReason: z.string().nullable(),
    validFrom: isoDateTimeSchema.nullable(),
    validUntil: isoDateTimeSchema.nullable(),
    internalNote: z.string().nullable(),
    source: customerContactPreferenceSourceSchema,
    capturedAt: isoDateTimeSchema,
    createdById: idSchema.nullable(),
    updatedById: idSchema.nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
    isCurrentlyEffective: z.boolean(),
});
export const resolvedCustomerContactPreferenceSchema = z.object({
    customerId: idSchema,
    contactId: idSchema.nullable(),
    preferredChannel: customerContactChannelSchema.nullable(),
    preferredTimeStart: localContactTimeSchema.nullable(),
    preferredTimeEnd: localContactTimeSchema.nullable(),
    preferredWeekdays: z.array(z.number().int().min(1).max(7)),
    allowedChannels: z.array(customerContactChannelSchema),
    blockedChannels: z.array(customerContactChannelSchema),
    generalContactBlock: z.boolean(),
    marketingContactBlock: z.boolean(),
    isContactAllowed: z.boolean(),
    isMarketingAllowed: z.boolean(),
    activePreferenceIds: z.array(idSchema),
    blockReasons: z.array(z.string()),
});
export const customerContactPreferenceOverviewSchema = z.object({
    customerId: idSchema,
    customerPreference: customerContactPreferenceSchema.nullable(),
    resolvedCustomerPreference: resolvedCustomerContactPreferenceSchema,
    contacts: z.array(z.object({
        contactId: idSchema,
        contactName: z.string(),
        email: z.string().nullable(),
        phone: z.string().nullable(),
        preference: customerContactPreferenceSchema.nullable(),
        resolvedPreference: resolvedCustomerContactPreferenceSchema,
    })),
});
//# sourceMappingURL=customer.js.map